'use client';

import { c as _c } from "react/compiler-runtime";
import { BottomDrawer, Portal } from '@common/components';
import { useBodyScrollLock } from '@common/hooks';
import SvgDelete from '@common/icon/SvgDelete';
import { cn } from '@core/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { useId } from 'react';
import { getMainWrapperElement } from '@/app/_components/MainWrapperPortal';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useOverlayHistory } from '@/hooks/useOverlayHistory';
import type { ResponsiveDialogConfig } from '@/stores/ResponsiveDialogStore';
import { DonationModal } from '../molecule/DonationModal';
interface ResponsiveDialogProps {
  opened: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
  /** drawer 헤더에 표시될 제목 */
  title?: ResponsiveDialogConfig['title'];
  ariaLabel?: ResponsiveDialogConfig['ariaLabel'];
  /** 모달의 header 부분 엘리먼트 */
  modalHeader?: ResponsiveDialogConfig['modalHeader'];
  duration?: number;
  /** bottom 형태인지 full 형태인지 결정 */
  drawerType?: 'bottom' | 'full';
  /** true일 경우 drawer content 영역의 padding 제거 (모바일만 적용) */
  drawerNoPadding?: boolean;
  /** true일 경우 full drawer 기본 헤더 숨기기 */
  hideDrawerHeader?: boolean;
  /** true일 경우 모바일에서도 모달로 표시 */
  forceModal?: boolean;
  /** true일 경우 앱바 스타일 헤더 적용 (왼쪽 X, 가운데 타이틀) */
  isAppBarHeader?: boolean;
  skipOverlayHistory?: boolean;
  className?: {
    modal?: string;
    backdrop?: string;
    drawer?: string;
  };
}

/**
 * 데스크톱에서는 모달, 모바일에서는 드로워로 표시\
 * 직접 사용하기 보다는 openResponsiveDialog 유틸로 간결하게 사용
 */
export const ResponsiveDialog = t0 => {
  const $ = _c(45);
  const {
    opened,
    onClose,
    children,
    modalHeader,
    title,
    ariaLabel,
    duration: t1,
    drawerType: t2,
    drawerNoPadding,
    hideDrawerHeader,
    forceModal,
    isAppBarHeader,
    skipOverlayHistory,
    className
  } = t0;
  const duration = t1 === undefined ? 300 : t1;
  const drawerType = t2 === undefined ? "full" : t2;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const drawerTitleId = useId();
  let t3;
  if ($[0] !== onClose) {
    t3 = () => onClose?.();
    $[0] = onClose;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  useOverlayHistory(opened && !!onClose && !skipOverlayHistory, t3);
  let t4;
  bb0: {
    if (title) {
      t4 = title;
      break bb0;
    }
    if (typeof modalHeader === "string") {
      t4 = modalHeader;
      break bb0;
    }
    if (modalHeader && typeof modalHeader === "object" && "title" in modalHeader) {
      t4 = modalHeader.title;
      break bb0;
    }
    t4 = undefined;
  }
  const drawerTitle = t4;
  if (isDesktopViewport || forceModal) {
    const t5 = className?.modal;
    let t6;
    if ($[2] !== children || $[3] !== modalHeader || $[4] !== onClose || $[5] !== opened || $[6] !== t5) {
      t6 = <DonationModal opened={opened} onClose={onClose} className={t5} header={modalHeader}>{children}</DonationModal>;
      $[2] = children;
      $[3] = modalHeader;
      $[4] = onClose;
      $[5] = opened;
      $[6] = t5;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    return t6;
  }
  if (drawerType === "bottom") {
    const t5 = title && !hideDrawerHeader ? undefined : ariaLabel;
    const t6 = title && !hideDrawerHeader ? drawerTitleId : undefined;
    const t7 = className?.backdrop;
    let t8;
    if ($[8] !== t7) {
      t8 = cn("fixed z-40 bg-Gray-black/60", t7);
      $[8] = t7;
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    const t9 = className?.drawer;
    let t10;
    if ($[10] !== t9) {
      t10 = cn("fixed z-50 rounded-t-xlarge", t9);
      $[10] = t9;
      $[11] = t10;
    } else {
      t10 = $[11];
    }
    let t11;
    if ($[12] !== t10 || $[13] !== t8) {
      t11 = {
        backdrop: t8,
        drawer: t10
      };
      $[12] = t10;
      $[13] = t8;
      $[14] = t11;
    } else {
      t11 = $[14];
    }
    let t12;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <BottomDrawer.HandleWrapper><BottomDrawer.Handle /></BottomDrawer.HandleWrapper>;
      $[15] = t12;
    } else {
      t12 = $[15];
    }
    const t13 = !drawerNoPadding;
    let t14;
    if ($[16] !== drawerNoPadding || $[17] !== t13) {
      t14 = cn({
        "mx-20": t13,
        "mx-0": drawerNoPadding
      });
      $[16] = drawerNoPadding;
      $[17] = t13;
      $[18] = t14;
    } else {
      t14 = $[18];
    }
    let t15;
    if ($[19] !== drawerNoPadding || $[20] !== drawerTitle || $[21] !== drawerTitleId || $[22] !== hideDrawerHeader) {
      t15 = drawerTitle && !hideDrawerHeader ? <h2 id={drawerTitleId} className={cn("mb-28 whitespace-pre-line font-700 text-Gray-900 typo-body1", drawerNoPadding && "mx-20")}>{drawerTitle}</h2> : null;
      $[19] = drawerNoPadding;
      $[20] = drawerTitle;
      $[21] = drawerTitleId;
      $[22] = hideDrawerHeader;
      $[23] = t15;
    } else {
      t15 = $[23];
    }
    let t16;
    if ($[24] !== children || $[25] !== t14 || $[26] !== t15) {
      t16 = <BottomDrawer.Contents className={t14}>{t15}{children}</BottomDrawer.Contents>;
      $[24] = children;
      $[25] = t14;
      $[26] = t15;
      $[27] = t16;
    } else {
      t16 = $[27];
    }
    let t17;
    if ($[28] !== onClose || $[29] !== opened || $[30] !== t11 || $[31] !== t16 || $[32] !== t5 || $[33] !== t6) {
      t17 = <BottomDrawer opened={opened} onClose={onClose} aria-label={t5} aria-labelledby={t6} unmountOnExit={true} renderTo={getMainWrapperElement} className={t11}>{t12}{t16}</BottomDrawer>;
      $[28] = onClose;
      $[29] = opened;
      $[30] = t11;
      $[31] = t16;
      $[32] = t5;
      $[33] = t6;
      $[34] = t17;
    } else {
      t17 = $[34];
    }
    return t17;
  }
  let t5;
  if ($[35] !== children || $[36] !== className || $[37] !== drawerNoPadding || $[38] !== drawerTitle || $[39] !== duration || $[40] !== hideDrawerHeader || $[41] !== isAppBarHeader || $[42] !== onClose || $[43] !== opened) {
    t5 = <FullDrawer opened={opened} onClose={onClose} title={drawerTitle} duration={duration} isAppBarHeader={isAppBarHeader} drawerNoPadding={drawerNoPadding} hideDrawerHeader={hideDrawerHeader} className={className}>{children}</FullDrawer>;
    $[35] = children;
    $[36] = className;
    $[37] = drawerNoPadding;
    $[38] = drawerTitle;
    $[39] = duration;
    $[40] = hideDrawerHeader;
    $[41] = isAppBarHeader;
    $[42] = onClose;
    $[43] = opened;
    $[44] = t5;
  } else {
    t5 = $[44];
  }
  return t5;
};
interface FullDrawerProps {
  opened: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
  title?: string;
  duration: number;
  isAppBarHeader?: boolean;
  drawerNoPadding?: boolean;
  hideDrawerHeader?: boolean;
  className?: {
    backdrop?: string;
    drawer?: string;
  };
}
const FullDrawer = t0 => {
  const $ = _c(13);
  const {
    opened,
    onClose,
    children,
    title,
    duration,
    isAppBarHeader,
    drawerNoPadding,
    hideDrawerHeader,
    className
  } = t0;
  const durationInSeconds = duration / 1000;
  useBodyScrollLock(opened);
  let t1;
  if ($[0] !== children || $[1] !== className?.backdrop || $[2] !== className?.drawer || $[3] !== drawerNoPadding || $[4] !== durationInSeconds || $[5] !== hideDrawerHeader || $[6] !== isAppBarHeader || $[7] !== onClose || $[8] !== opened || $[9] !== title) {
    t1 = opened && <><motion.div className={cn("fixed inset-0 z-40 bg-Gray-white", className?.backdrop)} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: durationInSeconds
      }} onClick={onClose} /><motion.div role="dialog" aria-modal="true" className={cn("fixed inset-0 z-50 flex flex-col bg-Gray-white", className?.drawer)} initial={{
        y: "20%",
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: "20%",
        opacity: 0
      }} transition={{
        type: "tween",
        duration: durationInSeconds,
        ease: "easeInOut"
      }}>{hideDrawerHeader ? null : <FullDrawerHeader title={title} isAppBarHeader={isAppBarHeader} onClose={onClose} />}<div className={cn("flex-1 overflow-auto", {
          "px-20": !drawerNoPadding,
          "px-0": drawerNoPadding
        })}>{children}</div></motion.div></>;
    $[0] = children;
    $[1] = className?.backdrop;
    $[2] = className?.drawer;
    $[3] = drawerNoPadding;
    $[4] = durationInSeconds;
    $[5] = hideDrawerHeader;
    $[6] = isAppBarHeader;
    $[7] = onClose;
    $[8] = opened;
    $[9] = title;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] !== t1) {
    t2 = <Portal renderTo={getMainWrapperElement}><AnimatePresence>{t1}</AnimatePresence></Portal>;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
};
interface FullDrawerHeaderProps {
  title?: string;
  isAppBarHeader?: boolean;
  onClose?: () => void;
}
const FullDrawerHeader = t0 => {
  const $ = _c(9);
  const {
    title,
    isAppBarHeader,
    onClose
  } = t0;
  const t1 = !isAppBarHeader;
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex min-h-56 items-center px-20", {
      "justify-between": t1
    });
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== isAppBarHeader || $[3] !== onClose || $[4] !== title) {
    t3 = isAppBarHeader ? <><button type="button" onClick={onClose} className="flex size-24 items-center justify-center" aria-label={"\uB2EB\uAE30"}><SvgDelete className="size-24 text-Gray-900" /></button>{title ? <h2 className="flex-1 text-center font-600 text-Gray-900 typo-body1">{title}</h2> : <div className="flex-1" />}<div className="size-24" /></> : <>{title ? <h2 className="font-700 text-Gray-900 typo-h6">{title}</h2> : <div />}<button type="button" onClick={onClose} className="flex size-32 items-center justify-center" aria-label={"\uB2EB\uAE30"}><SvgDelete /></button></>;
    $[2] = isAppBarHeader;
    $[3] = onClose;
    $[4] = title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <header className={t2}>{t3}</header>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};